import React from 'react'
import {
  Box,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Autocomplete
} from '@mui/material'
import { Delete as DeleteIcon } from '@mui/icons-material'
import type { LineItem, InvoiceFormData } from './types'

export type ProductOption = {
  value: string | number
  id: number
  label: string
  name: string
  uniqueKey: string
}

interface UseLineItemFieldsArgs {
  productOptions: ProductOption[]
  updateLineItem: (index: number, field: keyof LineItem, value: any) => void
  removeLineItem: (index: number) => void
  formData: InvoiceFormData
  disabled?: boolean
  currencyLabel?: string
}

export function useLineItemFields({
  productOptions,
  updateLineItem,
  removeLineItem,
  formData,
  disabled,
  currencyLabel
}: UseLineItemFieldsArgs) {
  const renderTableCell = (item: LineItem, index: number, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'product_code':
        return (
          <Autocomplete
            options={productOptions}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
            freeSolo
            value={null}
            onChange={(event, newValue) => {
              if (newValue && typeof newValue === 'object') {
                const productCode = String((newValue as ProductOption).value)
                updateLineItem(index, 'product_code', productCode)
                updateLineItem(index, 'product_name', (newValue as ProductOption).name)
              }
            }}
            inputValue={item.product_code || ''}
            onInputChange={(event, newInputValue, reason) => {
              updateLineItem(index, 'product_code', newInputValue || '')
              if (reason === 'clear' || !newInputValue) {
                updateLineItem(index, 'product_name', '')
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={item.product_code ? '' : 'ابحث بالرمز أو الاسم...'}
                size="small"
                fullWidth
                variant="standard"
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    color: item.product_code ? 'black' : 'inherit'
                  }
                }}
                disabled={!!disabled}
              />
            )}
            filterOptions={(options, { inputValue }) => {
              return (options as ProductOption[])
                .filter(
                  (option) =>
                    option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                    String(option.value).toLowerCase().includes(inputValue.toLowerCase()) ||
                    option.label.toLowerCase().includes(inputValue.toLowerCase())
                )
                .slice(0, 20)
            }}
            isOptionEqualToValue={(option, value) => (option as ProductOption).value === (value as ProductOption).value}
            noOptionsText="لا توجد نتائج"
            clearOnEscape
            blurOnSelect
            size="small"
            disabled={!!disabled}
          />
        )
      case 'product_name':
        return (
          <TextField
            value={item.product_name}
            onChange={(e) => updateLineItem(index, 'product_name', e.target.value)}
            size="small"
            fullWidth
            variant="standard"
            placeholder="تلقائي"
            InputProps={{ disableUnderline: true }}
            disabled={!!disabled}
          />
        )
      case 'serial_number':
        return (
          <TextField
            value={item.serial_number}
            onChange={(e) => updateLineItem(index, 'serial_number', e.target.value)}
            size="small"
            fullWidth
            variant="standard"
            InputProps={{ disableUnderline: true }}
            disabled={!!disabled}
          />
        )
      case 'quantity':
        return (
          <TextField
            type="number"
            value={item.quantity}
            onChange={(e) => updateLineItem(index, 'quantity', Number(e.target.value))}
            size="small"
            fullWidth
            variant="standard"
            InputProps={{ disableUnderline: true }}
            disabled={!!disabled}
          />
        )
      case 'carat':
        return (
          <TextField
            type="number"
            value={item.carat}
            onChange={(e) => updateLineItem(index, 'carat', Number(e.target.value))}
            size="small"
            fullWidth
            variant="standard"
            InputProps={{ disableUnderline: true }}
            disabled={!!disabled}
          />
        )
      case 'unit':
        return (
          <FormControl size="small" fullWidth variant="standard" disabled={!!disabled}>
            <Select value={item.unit} onChange={(e) => updateLineItem(index, 'unit', e.target.value)} displayEmpty disableUnderline>
              <MenuItem value="قطعة">قطعة</MenuItem>
              <MenuItem value="جرام">جرام</MenuItem>
              <MenuItem value="كيلو">كيلو</MenuItem>
              <MenuItem value="عدد">عدد</MenuItem>
            </Select>
          </FormControl>
        )
      case 'weight':
        return (
          <TextField
            type="number"
            value={item.weight}
            onChange={(e) => updateLineItem(index, 'weight', Number(e.target.value))}
            size="small"
            fullWidth
            variant="standard"
            InputProps={{ disableUnderline: true }}
            disabled={!!disabled}
          />
        )
      case 'stone_weight':
        return (
          <TextField
            type="number"
            value={item.stone_weight}
            onChange={(e) => updateLineItem(index, 'stone_weight', Number(e.target.value))}
            size="small"
            fullWidth
            variant="standard"
            InputProps={{ disableUnderline: true }}
            disabled={!!disabled}
          />
        )
      case 'unit_price':
        return (
          <TextField
            type="number"
            value={item.unit_price}
            onChange={(e) => updateLineItem(index, 'unit_price', Number(e.target.value))}
            size="small"
            fullWidth
            variant="standard"
            InputProps={{ disableUnderline: true }}
            disabled={!!disabled}
          />
        )
      case 'salary':
        return (
          <TextField
            type="number"
            value={item.salary}
            onChange={(e) => updateLineItem(index, 'salary', Number(e.target.value))}
            size="small"
            fullWidth
            variant="standard"
            InputProps={{ disableUnderline: true }}
            disabled={!!disabled}
          />
        )
      case 'total_salary':
        return (
          <TextField
            type="number"
            value={item.total_salary}
            onChange={(e) => updateLineItem(index, 'total_salary', Number(e.target.value))}
            size="small"
            fullWidth
            variant="standard"
            InputProps={{ disableUnderline: true }}
            disabled={!!disabled}
          />
        )
      case 'tax_rate':
        return (
          <TextField
            type="number"
            value={item.tax_rate}
            onChange={(e) => updateLineItem(index, 'tax_rate', Number(e.target.value))}
            size="small"
            fullWidth
            variant="standard"
            InputProps={{ disableUnderline: true }}
            disabled={!!disabled}
          />
        )
      case 'discount':
        return (
          <TextField
            type="number"
            value={item.discount}
            onChange={(e) => updateLineItem(index, 'discount', Number(e.target.value))}
            size="small"
            fullWidth
            variant="standard"
            InputProps={{ disableUnderline: true }}
            disabled={!!disabled}
          />
        )
      case 'total':
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              fontSize: '0.7rem',
              fontWeight: 'bold',
              color: 'white',
              bgcolor: 'primary.main',
              borderRadius: 0.5,
              px: 1,
              py: 0.5,
              border: '1px solid',
              borderColor: 'primary.dark'
            }}
          >
            {item.total.toFixed(2)}
          </Box>
        )
      case 'actions':
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <IconButton
              size="small"
              color="error"
              onClick={() => removeLineItem(index)}
              disabled={!!disabled || formData.line_items.length === 1}
              sx={{ padding: '2px' }}
            >
              <DeleteIcon sx={{ fontSize: '0.8rem' }} />
            </IconButton>
          </Box>
        )
      default:
        return null
    }
  }

  const renderMobileField = (item: LineItem, index: number, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'product_code':
        return (
          <Autocomplete
            options={productOptions}
            getOptionLabel={(option) => (typeof option === 'string' ? option : (option as ProductOption).label)}
            freeSolo
            value={null}
            onChange={(event, newValue) => {
              if (newValue && typeof newValue === 'object') {
                const opt = newValue as ProductOption
                updateLineItem(index, 'product_code', String(opt.value))
                updateLineItem(index, 'product_name', opt.name)
              }
            }}
            inputValue={item.product_code || ''}
            onInputChange={(event, newInputValue, reason) => {
              updateLineItem(index, 'product_code', newInputValue || '')
              if (reason === 'clear' || !newInputValue) {
                updateLineItem(index, 'product_name', '')
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="رمز الصنف"
                placeholder={item.product_code ? '' : 'ابحث بالرمز أو الاسم...'}
                size="small"
                fullWidth
                sx={{
                  '& .MuiInputBase-input': {
                    color: item.product_code ? 'black' : 'inherit'
                  }
                }}
                disabled={!!disabled}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={(option as ProductOption).uniqueKey}>
                <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <strong style={{ color: '#1976d2' }}>{(option as ProductOption).value}</strong>
                    <span style={{ color: '#333' }}>-</span>
                    <span style={{ color: '#333', fontWeight: 500 }}>{(option as ProductOption).name}</span>
                  </div>
                </div>
              </li>
            )}
            filterOptions={(options, { inputValue }) => {
              return (options as ProductOption[])
                .filter(
                  (option) =>
                    option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                    String(option.value).toLowerCase().includes(inputValue.toLowerCase()) ||
                    option.label.toLowerCase().includes(inputValue.toLowerCase())
                )
                .slice(0, 20)
            }}
            isOptionEqualToValue={(option, value) => (option as ProductOption).value === (value as ProductOption).value}
            noOptionsText="لا توجد نتائج"
            clearOnEscape
            blurOnSelect
            size="small"
          />
        )
      case 'product_name':
        return (
          <TextField
            label="اسم الصنف"
            value={item.product_name}
            onChange={(e) => updateLineItem(index, 'product_name', e.target.value)}
            size="small"
            fullWidth
            placeholder="يتم التعبئة تلقائياً"
            InputProps={{ readOnly: false }}
            disabled={!!disabled}
          />
        )
      case 'serial_number':
        return (
          <TextField
            label="رقم المسلسل"
            value={item.serial_number}
            onChange={(e) => updateLineItem(index, 'serial_number', e.target.value)}
            size="small"
            fullWidth
            disabled={!!disabled}
          />
        )
      case 'quantity':
        return (
          <TextField
            label="العدد"
            type="number"
            value={item.quantity}
            onChange={(e) => updateLineItem(index, 'quantity', Number(e.target.value))}
            size="small"
            fullWidth
            disabled={!!disabled}
          />
        )
      case 'carat':
        return (
          <TextField
            label="العيار"
            type="number"
            value={item.carat}
            onChange={(e) => updateLineItem(index, 'carat', Number(e.target.value))}
            size="small"
            fullWidth
            disabled={!!disabled}
          />
        )
      case 'unit':
        return (
          <FormControl size="small" fullWidth disabled={!!disabled}>
            <Select value={item.unit} onChange={(e) => updateLineItem(index, 'unit', e.target.value)}>
              <MenuItem value="قطعة">قطعة</MenuItem>
              <MenuItem value="جرام">جرام</MenuItem>
              <MenuItem value="كيلو">كيلو</MenuItem>
              <MenuItem value="عدد">عدد</MenuItem>
            </Select>
          </FormControl>
        )
      case 'weight':
        return (
          <TextField
            label="كمية / وزن"
            type="number"
            value={item.weight}
            onChange={(e) => updateLineItem(index, 'weight', Number(e.target.value))}
            size="small"
            fullWidth
            disabled={!!disabled}
          />
        )
      case 'stone_weight':
        return (
          <TextField
            label="وزن الفص"
            type="number"
            value={item.stone_weight}
            onChange={(e) => updateLineItem(index, 'stone_weight', Number(e.target.value))}
            size="small"
            fullWidth
            disabled={!!disabled}
          />
        )
      case 'unit_price':
        return (
          <TextField
            label="سعر البيع"
            type="number"
            value={item.unit_price}
            onChange={(e) => updateLineItem(index, 'unit_price', Number(e.target.value))}
            size="small"
            fullWidth
            disabled={!!disabled}
          />
        )
      case 'salary':
        return (
          <TextField
            label="الأجور"
            type="number"
            value={item.salary}
            onChange={(e) => updateLineItem(index, 'salary', Number(e.target.value))}
            size="small"
            fullWidth
            disabled={!!disabled}
          />
        )
      case 'total_salary':
        return (
          <TextField
            label="إجمالي الأجور"
            type="number"
            value={item.total_salary}
            onChange={(e) => updateLineItem(index, 'total_salary', Number(e.target.value))}
            size="small"
            fullWidth
            disabled={!!disabled}
          />
        )
      case 'tax_rate':
        return (
          <TextField
            label="نسبة الضريبة (%)"
            type="number"
            value={item.tax_rate}
            onChange={(e) => updateLineItem(index, 'tax_rate', Number(e.target.value))}
            size="small"
            fullWidth
            disabled={!!disabled}
          />
        )
      case 'discount':
        return (
          <TextField
            label="الخصم (%)"
            type="number"
            value={item.discount}
            onChange={(e) => updateLineItem(index, 'discount', Number(e.target.value))}
            size="small"
            fullWidth
            disabled={!!disabled}
          />
        )
      case 'total':
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 40,
              bgcolor: 'primary.main',
              borderRadius: 1,
              px: 2,
              border: '1px solid',
              borderColor: 'primary.dark'
            }}
          >
            <Typography variant="body2" fontWeight="bold" color="white">
              الإجمالي: {item.total.toFixed(2)} {currencyLabel ?? formData.currency}
            </Typography>
          </Box>
        )
      default:
        return null
    }
  }

  return { renderTableCell, renderMobileField }
}
